import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GradeLevel from './GradeLevel';
import ClassManagement from './ClassManagement';
import UserManagement from './UserManagement';
import ObjectiveManagement from './ObjectiveManagement';
import SummaryTab from './SummaryTab';
import AssignmentManagement from './AssignmentManagement';

const OWNER_EMAILS = ['phitaylr@gmail.com', 'ptaylor@students.randolphschool.net'];

function AdminDashboard({ user, discipline = 'stem' }) {
  const isOwner = !!(user?.email && OWNER_EMAILS.includes(String(user.email).toLowerCase()));
  const [activeSection, setActiveSection] = useState(isOwner ? 'summary' : 'dashboard');
  const [activeGrade, setActiveGrade] = useState(0);
  const [classes, setClasses] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [grades, setGrades] = useState([]);
  const [objectivesData, setObjectivesData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    // Reset to dashboard if not owner and somehow on summary
    if (!isOwner && activeSection === 'summary') {
      setActiveSection('dashboard');
    }
  }, [discipline, isOwner]);

  const loadData = async () => {
    try {
      console.log('AdminDashboard.loadData - Starting data load for discipline:', discipline);
      console.log('AdminDashboard - User:', user);
      console.log('AdminDashboard - Making API requests...');
      
      const [classesRes, allClassesRes, evidenceRes, objectivesRes] = await Promise.all([
        axios.get('/outcomes/api/classes', { params: { discipline }, withCredentials: true }),
        axios.get('/outcomes/api/classes', { params: { showAll: true }, withCredentials: true }),
        axios.get('/outcomes/api/evidence', { params: { discipline }, withCredentials: true }),
        axios.get('/outcomes/api/objectives-hierarchy', { params: { discipline }, withCredentials: true })
      ]);
      
      console.log('AdminDashboard - All API calls successful');
      console.log('AdminDashboard - Classes:', classesRes.data);
      console.log('AdminDashboard - All Classes:', allClassesRes.data);
      console.log('AdminDashboard - Evidence:', evidenceRes.data);
      console.log('AdminDashboard - Objectives:', objectivesRes.data);
      
      setClasses(allClassesRes.data);
      setEvidence(evidenceRes.data);
      setGrades(objectivesRes.data);
      setObjectivesData(objectivesRes.data);
    } catch (error) {
      console.error('AdminDashboard.loadData - Failed to load data:', error);
      console.error('AdminDashboard - Error response status:', error.response?.status);
      console.error('AdminDashboard - Error response data:', error.response?.data);
      console.error('AdminDashboard - Error message:', error.message);
      
      // If we get unauthorized, the session might be invalid
      if (error.response?.status === 401) {
        console.error('AdminDashboard - Not authenticated. User session may be invalid.');
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshClasses = async () => {
    try {
      const response = await axios.get('/outcomes/api/classes', { params: { showAll: true }, withCredentials: true });
      setClasses(response.data);
    } catch (error) {
      console.error('Failed to refresh classes:', error);
    }
  };

  const refreshEvidence = async () => {
    try {
      const response = await axios.get('/outcomes/api/evidence', { params: { discipline }, withCredentials: true });
      setEvidence(response.data);
    } catch (error) {
      console.error('Failed to refresh evidence:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <ClassManagement 
        classes={classes} 
        discipline={discipline}
        onClassesChange={refreshClasses}
        user={user}
      />
      
      <div className="dashboard-content">
        {isOwner && (
          <div className="section-selector">
            <button
              className={`section-btn ${activeSection === 'summary' ? 'active' : ''}`}
              onClick={() => setActiveSection('summary')}
            >
              Summary
            </button>
            <button
              className={`section-btn ${activeSection === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveSection('dashboard')}
            >
              Dashboard
            </button>
            <button
              className={`section-btn ${activeSection === 'objectives' ? 'active' : ''}`}
              onClick={() => setActiveSection('objectives')}
            >
              Manage Objectives
            </button>
            <button
              className={`section-btn ${activeSection === 'users' ? 'active' : ''}`}
              onClick={() => setActiveSection('users')}
            >
              Manage Users
            </button>
            <button
              className={`section-btn ${activeSection === 'assignments' ? 'active' : ''}`}
              onClick={() => setActiveSection('assignments')}
            >
              Manage Assignments
            </button>
          </div>
        )}

        {activeSection === 'summary' ? (
          <SummaryTab 
            classes={classes}
            evidence={evidence}
            grades={grades}
            discipline={discipline}
          />
        ) : activeSection === 'dashboard' ? (
          <>
            {grades.length === 0 ? (
              <div className="empty-state-container">
                <div className="empty-state">
                  <h3>No Grade Levels</h3>
                  <p>This discipline has no grade levels yet. Create one in the <strong>Manage Objectives</strong> section.</p>
                  {isOwner && (
                    <button className="btn-primary" onClick={() => setActiveSection('objectives')}>
                      Go to Manage Objectives
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="tabs">
                  {grades.map((grade, index) => (
                    <button
                      key={grade.id}
                      className={`tab ${activeGrade === index ? 'active' : ''}`}
                      onClick={() => setActiveGrade(index)}
                    >
                      {grade.title.split(':')[0]}
                    </button>
                  ))}
                </div>

                <div className="grade-content">
                  <GradeLevel
                    grade={grades[activeGrade]}
                    user={user}
                    classes={classes}
                    evidence={evidence}
                    discipline={discipline}
                    onEvidenceChange={refreshEvidence}
                    readOnly={false}
                  />
                </div>
              </>
            )}
          </>
        ) : activeSection === 'objectives' ? (
          <ObjectiveManagement discipline={discipline} />
        ) : activeSection === 'assignments' ? (
          <AssignmentManagement user={user} discipline={discipline} />
        ) : (
          <UserManagement user={user} discipline={discipline} />
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;